<template>
    <div class="diaspora-page">
        <!-- Hero — value proposition + services dashboard -->
        <section class="diaspora-hero">
            <div class="hero-overlay"></div>
            <div class="container hero-inner">
                <div class="text-center hero-copy">
                    <h1 class="hero-title">Stop Sending Cash.<br>Send What They Actually Need.</h1>
                    <p class="hero-subtitle">
                        Buy electricity, airtime, Wi-Fi, event tickets, and pay bills directly for your family in Zimbabwe.
                        You pick it, you pay — they receive it instantly. No middlemen. No guesswork.
                    </p>
                    <div class="hero-pills">
                        <span class="hero-pill"><i class="fas fa-bolt mr-1"></i>Instant delivery</span>
                        <span class="hero-pill"><i class="fas fa-hand-holding-heart mr-1"></i>Goes exactly where you intend</span>
                        <span class="hero-pill"><i class="fas fa-user-shield mr-1"></i>Easy for elderly parents</span>
                    </div>
                </div>

                <!-- Services Grid -->
                <div class="row services-grid justify-content-center">
                    <div class="col-6 col-md-4 col-lg-2 mb-3" v-for="service in services" :key="service.title">
                        <router-link :to="service.link" class="service-tile d-block text-decoration-none h-100">
                            <div class="service-icon-wrap" :style="{ background: service.gradient }">
                                <i :class="service.icon" class="service-icon"></i>
                            </div>
                            <h2 class="service-name">{{ service.title }}</h2>
                            <p class="service-desc">{{ service.desc }}</p>
                            <span class="service-action">{{ service.action }} <i class="fas fa-arrow-right ml-1"></i></span>
                        </router-link>
                    </div>
                </div>

                <div class="text-center mt-3">
                    <p class="hero-trust"><i class="fas fa-lock mr-1"></i> Secure payments via Visa &amp; Mastercard · Powered by Stripe</p>
                </div>
            </div>
        </section>

        <!-- Peace-of-mind value strip -->
        <section class="value-strip">
            <div class="container">
                <div class="value-strip-inner">
                    <div class="value-item" v-for="val in valueProps" :key="val.title">
                        <i :class="val.icon" class="value-item-icon"></i>
                        <div>
                            <h3 class="value-item-title">{{ val.title }}</h3>
                            <p class="value-item-desc">{{ val.desc }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- How It Works -->
        <section class="py-4 bg-white">
            <div class="container">
                <h2 class="section-title text-center mb-3" style="font-size:1.2rem;">How It Works</h2>
                <div class="how-it-works">
                    <div class="hw-step" v-for="(step, i) in steps" :key="i">
                        <div class="hw-num">{{ i + 1 }}</div>
                        <div>
                            <h3 class="hw-title">{{ step.title }}</h3>
                            <p class="hw-desc">{{ step.desc }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Product Detail Cards -->
        <section class="py-5 bg-light-section">
            <div class="container">
                <h2 class="section-title text-center mb-4">What You Can Send Home</h2>

                <div class="row">
                    <div class="col-md-4 mb-4" v-for="product in products" :key="product.title">
                        <div class="product-card h-100">
                            <div class="product-icon-wrap mb-3" :style="{ background: product.gradient }">
                                <i :class="product.icon" class="product-icon"></i>
                            </div>
                            <h3 class="product-name">{{ product.title }}</h3>
                            <p class="product-desc">{{ product.desc }}</p>
                            <div class="mt-auto">
                                <router-link :to="product.link" :class="['btn btn-sm shadow-none font-weight-bold', product.btnClass]">
                                    {{ product.action }} <i class="fas fa-arrow-right ml-1"></i>
                                </router-link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Why Magetsi -->
        <section class="py-5 bg-white">
            <div class="container">
                <h2 class="section-title text-center mb-4">Why Diaspora Zimbabweans Choose Magetsi</h2>
                <div class="row">
                    <div class="col-6 col-md-3 mb-4" v-for="reason in reasons" :key="reason.title">
                        <div class="reason-card text-center h-100">
                            <div class="reason-icon-wrap mx-auto mb-3">
                                <i :class="reason.icon" class="reason-icon"></i>
                            </div>
                            <h3 class="reason-title">{{ reason.title }}</h3>
                            <p class="reason-desc">{{ reason.desc }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Payment Banner -->
        <section class="py-5 bg-light-section">
            <div class="container">
                <div class="payment-banner">
                    <h2 class="payment-title">Simple Payments, Global Access</h2>
                    <p class="payment-desc">
                        Paying on Magetsi is as easy as shopping online. We accept international Visa and Mastercard —
                        you pay in USD and your support is delivered instantly.
                    </p>
                    <div class="payment-cards">
                        <div class="payment-badge"><i class="fab fa-cc-visa"></i></div>
                        <div class="payment-badge"><i class="fab fa-cc-mastercard"></i></div>
                        <div class="payment-badge"><i class="fas fa-shield-alt"></i></div>
                    </div>
                </div>
            </div>
        </section>

        <!-- SEO Content -->
        <section class="py-5 bg-white">
            <div class="container">
                <div class="seo-content mx-auto">
                    <h2 class="section-title mb-3">Supporting Your Family in Zimbabwe from the Diaspora</h2>
                    <p>
                        Millions of Zimbabweans living abroad in the UK, South Africa, the United States, Australia, Canada, and across
                        Europe send support home every month. But traditional remittance channels come with high fees, delays, and
                        uncertainty about whether the money will be used as intended. Magetsi offers a better way — instead
                        of sending cash, you pay directly for what your family needs.
                    </p>
                    <p>
                        When you buy ZESA tokens, airtime, data bundles, or TelOne Wi-Fi through Magetsi, the product is delivered
                        instantly and directly to your family member's meter, phone, or account. There's no middleman, no conversion
                        losses, and no risk of the funds being redirected. You pay online with your Visa or Mastercard in USD, and your
                        loved one receives exactly what you paid for — within seconds.
                    </p>

                    <h2 class="section-title mt-5 mb-3">How to Buy ZESA, Airtime &amp; Wi-Fi for Someone in Zimbabwe</h2>
                    <p>
                        Whether you're buying ZESA electricity for your parents, sending airtime to your siblings, or topping up TelOne
                        Wi-Fi for your children, the process is the same:
                    </p>
                    <ol>
                        <li>Visit <router-link to="/">magetsi.co.zw</router-link> and choose the product you want to send.</li>
                        <li>Enter the recipient's details — their meter number, phone number, or TelOne account.</li>
                        <li>Pay securely using your international Visa or Mastercard.</li>
                        <li>Done — the product is delivered to your loved one instantly.</li>
                    </ol>
                    <p>
                        No app download required. No registration needed. It works from any browser, on any device, from anywhere in the
                        world. It's especially ideal for taking care of elderly parents who may not be tech-savvy — you handle everything
                        online, and they simply enjoy the electricity, airtime, or internet you've sent.
                    </p>

                    <h2 class="section-title mt-5 mb-3">Trusted by Zimbabweans Worldwide</h2>
                    <p>
                        Magetsi is trusted by thousands of Zimbabweans in the diaspora to pay bills and send support back home.
                        Our platform is fast, reliable, and built specifically for the Zimbabwean market. Whether you're sending
                        ZESA tokens from London, airtime from Johannesburg, or Wi-Fi top-ups from New York, Magetsi makes it
                        simple and instant.
                    </p>
                </div>
            </div>
        </section>

        <!-- FAQ -->
        <section class="py-5 bg-light-section">
            <div class="container">
                <div class="faq-section mx-auto">
                    <h2 class="section-title text-center mb-4">Frequently Asked Questions</h2>
                    <div class="faq-list">
                        <div class="faq-item" v-for="(faq, i) in faqs" :key="i">
                            <div class="faq-question" @click="toggleFaq(i)">
                                <h3 class="faq-q-text">{{ faq.q }}</h3>
                                <i :class="openFaq === i ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="faq-chevron"></i>
                            </div>
                            <transition name="faq-slide">
                                <div v-if="openFaq === i" class="faq-answer">
                                    <p v-html="faq.a"></p>
                                </div>
                            </transition>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Final CTA -->
        <section class="diaspora-cta">
            <div class="container text-center">
                <h2 class="cta-title">Show Love From Wherever You Are</h2>
                <p class="cta-subtitle">
                    Whether you're in South Africa, the UK, the US, or beyond — you're just a few clicks away from
                    directly supporting your family in Zimbabwe.
                </p>
                <div class="cta-actions">
                    <router-link to="/zesa" class="btn btn-warning btn-lg cta-btn">
                        <i class="fas fa-bolt mr-2"></i>Buy ZESA
                    </router-link>
                    <router-link to="/airtime" class="btn btn-success btn-lg cta-btn">
                        <i class="fas fa-phone-alt mr-2"></i>Send Airtime
                    </router-link>
                    <a href="https://api.whatsapp.com/send/?phone=263785832033&text=Hi+Magetsi" target="_blank" class="btn btn-whatsapp btn-lg cta-btn">
                        <i class="fab fa-whatsapp mr-2"></i>Chat on WhatsApp
                    </a>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
export default {
    name: "DiasporaLanding",
    metaInfo() {
        return {
            title: 'Buy ZESA, Airtime & Wi-Fi for Family in Zimbabwe',
        };
    },
    data() {
        return {
            openFaq: null,
            services: [
                {
                    title: 'ZESA Tokens',
                    desc: 'Keep the lights on at home',
                    action: 'Buy ZESA',
                    icon: 'fas fa-bolt',
                    gradient: 'linear-gradient(135deg, #f59e0b, #f97316)',
                    link: '/zesa',
                },
                {
                    title: 'Airtime',
                    desc: 'Econet & NetOne top-ups',
                    action: 'Send Airtime',
                    icon: 'fas fa-mobile-alt',
                    gradient: 'linear-gradient(135deg, #28a745, #20c997)',
                    link: '/airtime',
                },
                {
                    title: 'Data Bundles',
                    desc: 'Mobile data for their phone',
                    action: 'Send Bundles',
                    icon: 'fas fa-wifi',
                    gradient: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                    link: '/bundles',
                },
                {
                    title: 'TelOne Wi-Fi',
                    desc: 'Home internet top-up',
                    action: 'Buy Wi-Fi',
                    icon: 'fas fa-globe',
                    gradient: 'linear-gradient(135deg, #1a56db, #4a7cfb)',
                    link: '/telone',
                },
                {
                    title: 'Event Tickets',
                    desc: 'Concerts, shows & more',
                    action: 'Buy Tickets',
                    icon: 'fas fa-ticket-alt',
                    gradient: 'linear-gradient(135deg, #e11d48, #f43f5e)',
                    link: '/tickets',
                },
                {
                    title: 'Pay Bills',
                    desc: 'Donations, Subscriptions & more',
                    action: 'Pay a Bill',
                    icon: 'fas fa-file-invoice-dollar',
                    gradient: 'linear-gradient(135deg, #06b6d4, #0891b2)',
                    link: '/billers',
                },
            ],
            valueProps: [
                {
                    title: 'Your Support. Their Doorstep.',
                    desc: 'Stop worrying about whether cash arrives or gets used right. With Magetsi, you choose exactly what your family gets — electricity, airtime, internet, or bills — and it\'s delivered directly.',
                    icon: 'fas fa-hand-holding-heart',
                },
                {
                    title: 'Even Gogo Can Benefit',
                    desc: 'Your parents or grandparents don\'t need to be tech-savvy. You handle everything online from wherever you are — they just enjoy the lights, the airtime, or the Wi-Fi.',
                    icon: 'fas fa-user-shield',
                },
                {
                    title: 'Peace of Mind, Guaranteed',
                    desc: 'Every token, every top-up, every bill payment is confirmed instantly. You\'ll always know your support went exactly where you intended.',
                    icon: 'fas fa-check-circle',
                },
            ],
            steps: [
                { title: 'Choose a product', desc: 'ZESA, airtime, bundles, Wi-Fi, tickets, or bills.' },
                { title: 'Enter their details', desc: 'Meter number, phone number, or account.' },
                { title: 'Pay with your card', desc: 'Visa or Mastercard — processed in USD.' },
                { title: 'Delivered instantly', desc: 'They receive it within seconds.' },
            ],
            products: [
                {
                    title: 'ZESA Electricity',
                    desc: 'Put a smile on their faces by keeping the power running. Enter their meter number, pay with your card, and the ZESA token is delivered instantly via SMS. They simply enter it into their prepaid meter.',
                    icon: 'fas fa-bolt',
                    gradient: 'linear-gradient(135deg, #f59e0b, #f97316)',
                    link: '/zesa',
                    action: 'Buy & Send ZESA',
                    btnClass: 'btn-warning',
                },
                {
                    title: 'Airtime & Bundles',
                    desc: 'Stay connected with your loved ones. Enter their Zimbabwean phone number, pay online, and they receive airtime or data bundles instantly. Works for Econet and NetOne.',
                    icon: 'fas fa-mobile-alt',
                    gradient: 'linear-gradient(135deg, #28a745, #20c997)',
                    link: '/airtime',
                    action: 'Send Airtime',
                    btnClass: 'btn-success',
                },
                {
                    title: 'TelOne Wi-Fi',
                    desc: 'Help your family stay connected with reliable home internet. Top up their TelOne Wi-Fi directly — fast online recharge from anywhere in the world.',
                    icon: 'fas fa-globe',
                    gradient: 'linear-gradient(135deg, #1a56db, #4a7cfb)',
                    link: '/telone',
                    action: 'Buy TelOne Wi-Fi',
                    btnClass: 'btn-primary',
                },
                {
                    title: 'Event Tickets',
                    desc: 'Buy tickets for concerts, festivals, and events happening in Zimbabwe. Gift your loved ones a great experience — purchase and send tickets from anywhere.',
                    icon: 'fas fa-ticket-alt',
                    gradient: 'linear-gradient(135deg, #e11d48, #f43f5e)',
                    link: '/tickets',
                    action: 'Browse Events',
                    btnClass: 'btn-danger',
                },
                {
                    title: 'Bill Payments',
                    desc: 'Pay donations, subscriptions, and other bills for your family directly. Select the biller, enter account details, and pay with your card.',
                    icon: 'fas fa-file-invoice-dollar',
                    gradient: 'linear-gradient(135deg, #06b6d4, #0891b2)',
                    link: '/billers',
                    action: 'Pay a Bill',
                    btnClass: 'btn-info',
                },
            ],
            reasons: [
                {
                    title: 'Instant Delivery',
                    desc: 'Tokens, airtime, and Wi-Fi are delivered within seconds.',
                    icon: 'fas fa-paper-plane',
                },
                {
                    title: 'Direct Support',
                    desc: 'Pay for exactly what they need. No cash to go missing.',
                    icon: 'fas fa-hand-holding-heart',
                },
                {
                    title: 'Visa & Mastercard',
                    desc: 'Pay with your international card from any country.',
                    icon: 'fas fa-credit-card',
                },
                {
                    title: '24/7 Available',
                    desc: 'Buy anytime — day or night, any timezone.',
                    icon: 'fas fa-clock',
                },
            ],
            faqs: [
                {
                    q: 'Can I buy ZESA tokens from outside Zimbabwe?',
                    a: 'Yes! You can buy ZESA tokens for any meter in Zimbabwe from anywhere in the world using your Visa or Mastercard. The token is delivered instantly via SMS to the recipient\'s phone.',
                },
                {
                    q: 'How do I send airtime to someone in Zimbabwe?',
                    a: 'Visit <a href="/airtime">the airtime page</a>, enter the recipient\'s Zimbabwean phone number (Econet or NetOne), pay with your Visa or Mastercard, and they receive the airtime instantly.',
                },
                {
                    q: 'What payment methods do you accept?',
                    a: 'We accept international Visa and Mastercard debit and credit cards. Payments are processed in USD securely through Stripe.',
                },
                {
                    q: 'How does my family receive the ZESA token?',
                    a: 'When you buy a ZESA token, you add a mobile number as the recipient. The 20-digit token is sent via SMS to that number. They simply enter it into their prepaid meter.',
                },
                {
                    q: 'Can I buy for someone who is not tech-savvy?',
                    a: 'Absolutely. You handle the entire purchase online — your family member doesn\'t need to do anything. For ZESA, the token is sent via SMS. For airtime and bundles, it\'s credited directly to their phone. For TelOne, the Wi-Fi is topped up automatically.',
                },
                {
                    q: 'Which countries can I buy from?',
                    a: 'Any country — UK, South Africa, USA, Canada, Australia, Germany, Ireland, and beyond. All you need is an internet connection and a Visa or Mastercard.',
                },
                {
                    q: 'Is Magetsi safe and trustworthy?',
                    a: 'Yes. Magetsi is trusted by thousands of Zimbabweans both locally and in the diaspora. Payments are processed securely through Stripe, and we deliver products directly through official channels (ZETDC for ZESA, Econet/NetOne for airtime).',
                },
            ],
        };
    },
    methods: {
        toggleFaq(index) {
            this.openFaq = this.openFaq === index ? null : index;
        },
    },
};
</script>

<style scoped>
/* Hero — compact, services-first */
.diaspora-hero {
    position: relative;
    background: linear-gradient(135deg, #1a3c34 0%, #0f6b3a 50%, #28a745 100%);
    padding: 36px 0 28px;
    overflow: hidden;
}
.hero-overlay {
    position: absolute;
    inset: 0;
    background: url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.04'%3E%3Cpath d='M50 50c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10s-10-4.477-10-10 4.477-10 10-10zM10 10c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10S0 25.523 0 20s4.477-10 10-10z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}
.hero-inner {
    position: relative;
    z-index: 1;
}
.hero-copy {
    margin-bottom: 28px;
}
.hero-title {
    color: #fff;
    font-weight: 800;
    font-size: 1.55rem;
    margin-bottom: 12px;
    line-height: 1.3;
}
.hero-subtitle {
    color: rgba(255,255,255,0.88);
    font-size: 0.95rem;
    max-width: 560px;
    margin: 0 auto 16px;
    line-height: 1.6;
}
.hero-pills {
    display: flex;
    justify-content: center;
    gap: 10px;
    flex-wrap: wrap;
}
.hero-pill {
    background: rgba(255,255,255,0.13);
    border: 1px solid rgba(255,255,255,0.18);
    border-radius: 20px;
    padding: 5px 14px;
    font-size: 0.73rem;
    font-weight: 600;
    color: rgba(255,255,255,0.92);
    white-space: nowrap;
}
.hero-trust {
    color: rgba(255,255,255,0.5);
    font-size: 0.75rem;
    margin-bottom: 0;
}

/* Service Tiles */
.services-grid {
    max-width: 900px;
    margin: 0 auto;
}
.service-tile {
    background: rgba(255,255,255,0.12);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255,255,255,0.15);
    border-radius: 14px;
    padding: 20px 14px 16px;
    text-align: center;
    transition: all 0.25s ease;
    cursor: pointer;
    color: #fff;
}
.service-tile:hover {
    background: rgba(255,255,255,0.2);
    border-color: rgba(255,255,255,0.3);
    transform: translateY(-2px);
    color: #fff;
    text-decoration: none;
}
.service-icon-wrap {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 10px;
}
.service-icon {
    font-size: 1.15rem;
    color: #fff;
}
.service-name {
    font-weight: 700;
    font-size: 0.88rem;
    color: #fff;
    margin-bottom: 3px;
}
.service-desc {
    font-size: 0.72rem;
    color: rgba(255,255,255,0.7);
    margin-bottom: 8px;
    line-height: 1.4;
}
.service-action {
    font-size: 0.72rem;
    font-weight: 600;
    color: rgba(255,255,255,0.9);
    letter-spacing: 0.3px;
}

/* Value proposition strip */
.value-strip {
    background: #fff;
    border-bottom: 1px solid #e9ecef;
    padding: 32px 0;
}
.value-strip-inner {
    display: flex;
    gap: 32px;
    justify-content: center;
    flex-wrap: wrap;
}
.value-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    flex: 1;
    min-width: 240px;
    max-width: 320px;
}
.value-item-icon {
    font-size: 1.3rem;
    color: #28a745;
    margin-top: 2px;
    flex-shrink: 0;
}
.value-item-title {
    font-weight: 700;
    font-size: 0.92rem;
    color: #1a1a2e;
    margin-bottom: 4px;
}
.value-item-desc {
    font-size: 0.82rem;
    color: #4b5563;
    line-height: 1.55;
    margin-bottom: 0;
}

/* How It Works — inline strip */
.how-it-works {
    display: flex;
    gap: 24px;
    justify-content: center;
    flex-wrap: wrap;
    padding: 12px 0;
}
.hw-step {
    display: flex;
    align-items: center;
    gap: 10px;
}
.hw-num {
    width: 28px;
    height: 28px;
    min-width: 28px;
    border-radius: 50%;
    background: linear-gradient(135deg, #28a745, #20c997);
    color: #fff;
    font-size: 0.75rem;
    font-weight: 800;
    display: flex;
    align-items: center;
    justify-content: center;
}
.hw-title {
    font-weight: 700;
    font-size: 0.82rem;
    color: #1a1a2e;
    margin-bottom: 0;
}
.hw-desc {
    font-size: 0.72rem;
    color: #6b7280;
    margin-bottom: 0;
}

/* Section titles */
.section-title {
    font-weight: 700;
    font-size: 1.5rem;
    color: #1a1a2e;
}

/* Background sections */
.bg-light-section {
    background: #f7f8fc;
}

/* Product Detail Cards */
.product-card {
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 14px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    transition: box-shadow 0.25s;
}
.product-card:hover {
    box-shadow: 0 4px 20px rgba(0,0,0,0.06);
}
.product-icon-wrap {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
}
.product-icon {
    font-size: 1.1rem;
    color: #fff;
}
.product-name {
    font-weight: 700;
    font-size: 1.05rem;
    color: #1a1a2e;
    margin-bottom: 8px;
}
.product-desc {
    font-size: 0.88rem;
    color: #4b5563;
    line-height: 1.65;
    margin-bottom: 16px;
    flex: 1;
}

/* Reasons */
.reason-card {
    padding: 16px 8px;
}
.reason-icon-wrap {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background: linear-gradient(135deg, #e8f5e9, #c8e6c9);
    display: flex;
    align-items: center;
    justify-content: center;
}
.reason-icon {
    font-size: 1.15rem;
    color: #28a745;
}
.reason-title {
    font-weight: 700;
    font-size: 0.9rem;
    color: #1a1a2e;
    margin-bottom: 4px;
}
.reason-desc {
    font-size: 0.78rem;
    color: #6b7280;
    margin-bottom: 0;
    line-height: 1.5;
}

/* Payment Banner */
.payment-banner {
    background: linear-gradient(135deg, #f8faff, #eef2ff);
    border: 1px solid #dce3f5;
    border-radius: 14px;
    padding: 32px;
    text-align: center;
}
.payment-title {
    font-weight: 700;
    font-size: 1.3rem;
    color: #1a1a2e;
    margin-bottom: 10px;
}
.payment-desc {
    font-size: 0.95rem;
    color: #4b5563;
    max-width: 520px;
    margin: 0 auto 20px;
    line-height: 1.6;
}
.payment-cards {
    display: flex;
    justify-content: center;
    gap: 16px;
}
.payment-badge {
    width: 56px;
    height: 38px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.4rem;
    color: #374151;
}

/* SEO content */
.seo-content {
    max-width: 800px;
    font-size: 1rem;
    line-height: 1.75;
    color: #374151;
}
.seo-content h2 {
    font-size: 1.35rem;
}
.seo-content ol {
    padding-left: 20px;
}
.seo-content ol li {
    margin-bottom: 8px;
}
.seo-content a {
    color: #1a56db;
    text-decoration: underline;
}

/* FAQ */
.faq-section {
    max-width: 720px;
}
.faq-item {
    border: 1px solid #e9ecef;
    border-radius: 10px;
    margin-bottom: 10px;
    overflow: hidden;
    background: #fff;
}
.faq-question {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    cursor: pointer;
    transition: background 0.2s;
}
.faq-question:hover {
    background: #f9fafb;
}
.faq-q-text {
    font-size: 0.95rem;
    font-weight: 600;
    color: #1a1a2e;
    margin: 0;
    flex: 1;
    padding-right: 12px;
}
.faq-chevron {
    font-size: 0.75rem;
    color: #9ca3af;
    flex-shrink: 0;
}
.faq-answer {
    padding: 0 20px 16px;
    font-size: 0.9rem;
    color: #4b5563;
    line-height: 1.7;
}
.faq-answer a {
    color: #1a56db;
    text-decoration: underline;
}
.faq-slide-enter-active, .faq-slide-leave-active {
    transition: all 0.25s ease;
    max-height: 300px;
}
.faq-slide-enter, .faq-slide-leave-to {
    opacity: 0;
    max-height: 0;
    padding-top: 0;
    padding-bottom: 0;
}

/* Final CTA */
.diaspora-cta {
    background: linear-gradient(135deg, #1a3c34 0%, #0f6b3a 100%);
    padding: 50px 0;
}
.cta-title {
    color: #fff;
    font-weight: 800;
    font-size: 1.6rem;
    margin-bottom: 10px;
}
.cta-subtitle {
    color: rgba(255,255,255,0.85);
    font-size: 1rem;
    max-width: 520px;
    margin: 0 auto 24px;
    line-height: 1.6;
}
.cta-actions {
    display: flex;
    justify-content: center;
    gap: 12px;
    flex-wrap: wrap;
}
.cta-btn {
    font-weight: 600;
    font-size: 0.95rem;
    padding: 10px 24px;
    border-radius: 8px;
}
.btn-whatsapp {
    background: #25d366;
    border-color: #25d366;
    color: #fff;
    font-weight: 600;
    border-radius: 8px;
}
.btn-whatsapp:hover {
    background: #1fb855;
    border-color: #1fb855;
    color: #fff;
}

/* Mobile */
@media (max-width: 767px) {
    .diaspora-hero {
        padding: 28px 0 20px;
    }
    .hero-title {
        font-size: 1.2rem;
    }
    .hero-subtitle {
        font-size: 0.88rem;
    }
    .hero-pills {
        gap: 6px;
    }
    .hero-pill {
        font-size: 0.68rem;
        padding: 4px 10px;
    }
    .value-strip {
        padding: 24px 0;
    }
    .value-strip-inner {
        gap: 20px;
    }
    .value-item {
        min-width: 100%;
    }
    .service-tile {
        padding: 16px 10px 12px;
    }
    .service-icon-wrap {
        width: 42px;
        height: 42px;
        border-radius: 12px;
    }
    .service-icon {
        font-size: 1rem;
    }
    .service-name {
        font-size: 0.82rem;
    }
    .service-desc {
        font-size: 0.68rem;
    }
    .service-action {
        font-size: 0.68rem;
    }
    .section-title {
        font-size: 1.25rem;
    }
    .how-it-works {
        gap: 12px;
    }
    .hw-step {
        flex: 1 1 45%;
    }
    .reason-card {
        padding: 12px 4px;
    }
    .reason-icon-wrap {
        width: 42px;
        height: 42px;
    }
    .reason-icon {
        font-size: 1rem;
    }
    .reason-title {
        font-size: 0.82rem;
    }
    .reason-desc {
        font-size: 0.72rem;
    }
    .payment-banner {
        padding: 24px 16px;
    }
    .cta-btn {
        font-size: 0.88rem;
        padding: 10px 20px;
        width: 100%;
        max-width: 260px;
    }
}

@media (min-width: 768px) {
    .hero-title {
        font-size: 2rem;
    }
    .hero-subtitle {
        font-size: 1.05rem;
    }
    .services-grid {
        max-width: 960px;
    }
}
</style>
